<template>
  <v-container fluid>
    <v-row class="justify-center align-center ma-2 mt-4">
      <v-col cols="auto">
        <DateCard
          v-model:modelValue="dateRange"
          v-model:modelLocation="selectedLocation"
          :dateCardLocations="locations"
        />
      </v-col>
    </v-row>
    <v-row class="justify-center align-stretch ma-2 mt-n2">
      <v-col cols="auto" class="d-flex">
        <FavoritesCard
        :favoritesCardPremises="premises"
        :favoritesCardFavorites="favorites"
        :favoritesCardUsers="users"
        :favoritesCardStartDate="dateRange.start"
        :favoritesCardEndDate="dateRange.end"
        @openOccupancy="openOccupancy"
        @updateFavorite="refreshFavStatus"
        @updateStatus="refreshStatus"
        />
      </v-col>
      <v-col cols="auto">
        <SeatBookingCard
        :bookingCardPremises="premises"
        :bookingCardSeats="getAllSeats"
        :bookingCardStartDate="dateRange.start"
        :bookingCardEndDate="dateRange.end"
        :bookingCardUsers="users"
        @updateFavorite="refreshFavStatus"
        @updateStatus="refreshStatus"
        @openOccupancy="openOccupancy"
      />
      </v-col>
      <v-col class="d-flex flex-grow-1">
        <CalendarCard
        :calendarCardSeat="occupancySeat"
        :calendarCardUsers="users"
        @updateFavorite="refreshFavStatus"
        @updateSeatStatus="refreshStatus"
        @resetOccupancy="openOccupancy"/>
      </v-col>
    </v-row>
    <v-row class="justify-center align-stretch ma-2 mt-6">
      <v-col cols="auto">
        <TimeBookingCard
          :timeBookingCardLocations="locations"
          :timeBookingCardUsers="users"
          @updateReservation="refreshStatus(); openOccupancy()"/>
      </v-col>
      <v-col cols="auto" class="flex-grow-1">
        <ReservationsCard
        :reservationCardPremises="premises"
        :reservationCardReservations="getReservations"
        :reservationCardUsers="users"
        @updateFavorite="refreshFavStatus"
        @updateReservation="refreshStatus"
        @updateOccupancy="openOccupancy"/>
      </v-col>
      <v-col cols="auto">
        <ReleaseCard
          :relCardSeat="ownSeats"
          :relCardPremises="premises"
          @updateReleaseTimes="refreshStatus"
          @setOccupancy="openOccupancy"/>
      </v-col>
    </v-row>
  </v-container>
</template>
  
<script>
import DateCard from '../components/DateCard.vue'
import FavoritesCard from '../components/FavoritesCard.vue'
import SeatBookingCard from '../components/SeatBookingCard.vue'
import CalendarCard from '../components/CalendarCard.vue'
import TimeBookingCard from '../components/TimeBookingCard.vue'
import ReservationsCard from '../components/ReservationsCard.vue'
import ReleaseCard from '../components/ReleaseCard.vue'

export default {
  name: 'Dashboard',
  components: {
    DateCard, 
    SeatBookingCard, 
    FavoritesCard,
    TimeBookingCard, 
    ReservationsCard,
    ReleaseCard, 
    CalendarCard, 
  },

  data() {
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    const tomorrowStr = tomorrow.toISOString().slice(0, 10)

    return {
      dateRange: { start: tomorrowStr, end: tomorrowStr },
      locations: [],
      selectedLocation: '',
      premises: [],
      selectedPremise: null,
      seats: [],
      ownSeats: [],
      seatStatus: [],
      favorites: [],
      users: [],
      occupancy: [],
      occupancySeat: null,
      reservations: [],
    }
  },

  watch: {
    dateRange: {
      handler() { 
        this.loadSeatStatus()
      },
      deep: true,
      immediate: true
    },
    
    selectedLocation(){
      if(!this.selectedLocation) return
      this.loadPremises()
    }
  },

  async mounted() {
    await this.loadLocations()
    await this.loadPremises()
    await this.loadSeats()
    await this.loadUsers()
    await this.loadFavorites()
    await this.loadOwnReservations()
    this.getOwnerSeat()
    this.deleteSeatOccupancy()
  },

  computed: {
    availableSeats() {
      return this.filterAvailableSeats(this.seats, this.seatStatus, this.favorites)
    },
    getAllSeats() {
      return this.getSeats(this.seats, this.seatStatus, this.favorites)
    },
    getReservations(){
      return this.getReservationInfos(this.reservations, this.favorites)
    }
  },

  methods: {
    refreshStatus() {
      this.loadSeatStatus()
      this.loadFavorites()
      this.loadOwnReservations()
    },

    refreshFavStatus() {
      this.loadFavorites()
    },

    openOccupancy(payload) {
      this.occupancySeat = payload
    },

    deleteSeatOccupancy(){
      this.occupancySeat = null
    },

    async loadLocations() {
      try{ 
        const {data} = await this.$axios.get('/api/locations/')
        this.locations = data
      } catch (err) {
          console.error('Fehler beim Laden der Locations:', err)
      } 
    },

    async loadPremises() {
      try{
        const {data} = await this.$axios.get('/api/premises/', {
          params: { location: this.selectedLocation}
        })
        this.premises = data
      }catch (err) {
        console.error('Fehler beim Laden der Premises: ', err)
      }
    },

    async loadSeats() {
      if (!this.selectedLocation) return
      try {
        const { data } = await this.$axios.get('/api/seats/', {
          params: { location: this.selectedLocation }
        })
        this.seats = data
      } catch (err) {
        console.error('Fehler beim Laden der Sitze:', err)
      }
    },

    async loadSeatStatus() {
      try {
        const { data } = await this.$axios.get('/api/seats/status/', {
          params: {
            start: this.dateRange.start,
            end: this.dateRange.end,
          },
        })
        this.seatStatus = data
      } catch (err) {
        console.error('Fehler beim Laden der freien Sitze:', err)
      }
    },

    async loadUsers(){
      try{
        const { data } = await this.$axios.get('/api/users/')
        this.users = data
      } catch (err) {
        console.error('Fehler beim Laden der Benutzer: ', err)
      }
    },

    async loadFavorites() {
      try {
        const { data } = await this.$axios.get('/api/favorites/', { 
          params: { location: this.selectedLocation } 
        })
        this.favorites = data
      } catch (err) {
        console.error('Fehler beim Laden der Favoriten', err)
      } 
    },

    async loadOccupancy() {
      try {
        const { data } = await this.$axios.get(`/api/seats/${this.occupancySeat.id}/booked-dates/`)
        this.occupancy = data
      } catch (err) {
        console.error('Fehler beim Laden der Sitze:', err)
      }
    },

    async loadOwnReservations() {
      try {
        const { data } = await this.$axios.get(`/api/reservations/me/`)
        this.reservations = data
      } catch (err) {
        console.error('Fehler beim Laden der Reservierungen:', err)
      } 
    },

  filterAvailableSeats(seats, status, favorites) {
    const mapped = seats.map(seat => {
      const seatStatus = status.find(s => s.seat_id === seat.id) || {}
      const favorite = favorites.find(f => f.seat.id === seat.id)

      return {
        ...seat,
        isFavorite: !!favorite,
        favoriteID: favorite ? favorite.id : -1,
        fully_booked: seatStatus.fully_booked ?? false,
        partial_booked: seatStatus.partial_booked ?? false,
      }
    })

    return mapped.filter(s => !s.fully_booked)
  },

  getSeats(seats, status, favorites) {
    const fullMapped = seats.map(seat => {
      const seatStatus = status.find(s => s.seat_id === seat.id) || {}
      const favorite = favorites.find(f => f.seat.id === seat.id)

      return {
        ...seat,
        isFavorite: !!favorite,
        favoriteID: favorite ? favorite.id : -1,
        fully_booked: seatStatus.fully_booked ?? false,
        partial_booked: seatStatus.partial_booked ?? false,
        }
      })
    return fullMapped
    },

    getReservationInfos(reservations, favorites) {
      const fullMapped = reservations.map(reservation => {
        const favorite = favorites.find(f => f.seat.id === reservation.seat.id)
        
        return{
          ...reservation,
          isFavorite: !!favorite,
          favoriteID: favorite ? favorite.id : -1,
        }
      })
      return fullMapped
    },

    getOwnerSeat() {
      const seats = this.seats.filter(s => s.owner === this.users.current_user_id)
      this.ownSeats = seats
    }
  }
}
</script>

<style>
.full-center {
  display: flex;
  flex-grow: 1 1;
  justify-content: center;
  align-items: center;
  height: 100%;
}
</style>